import './header.scss';

import {
  Avatar,
  Badge,
  Dropdown,
  type DropdownProps,
  Space,
  Tooltip,
  notification
} from 'antd';
import { jwtDecode } from 'jwt-decode';
import { match } from 'path-to-regexp';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';

import {
  ADMIN_ROUTES,
  ROUTES,
  getFullAdminRoute
} from '@/components/Shared/routes/routes';
import { CircledImage } from '@/components/Shared/ui';
import { BANKS_ICONS_NAMES, DictTypes } from '@/constants';
import { DownOutlined } from '@/media/antd-icons';
import BellIcon from '@/media/icons/bell.svg?react';
import BroomIcon from '@/media/icons/broom.svg?react';
import ChevronDownIcon from '@/media/icons/chevron-down.svg?react';
import BurgerMenuIcon from '@/media/icons/menu-burger.svg?react';
import { removeCachedData } from '@/services/helpers/cacheData';
import { clearLocalStorageHelper } from '@/services/helpers/clearLocalStorage';
import { safeKey } from '@/services/helpers/dict';
import { getUserFullName } from '@/services/helpers/strings';
import { getUserToken } from '@/services/user-hash';
import { useAppDispatch, useAppSelector } from '@/store';
import { setIsAuth } from '@/store/auth.slice';
import { getOptimizedDictionaries } from '@/store/dict.slice';
import { onToggleShowNotifications } from '@/store/notifications.slice';
import { setSidebarExpanded } from '@/store/userInfo.slice';
import type { DecodedJWTToken } from '@/types/user';

import { getPageInfo, shouldShowBackArrow } from './helpers/get-page-info';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { lastNotificationsChecked } = useAppSelector(
    (state) => state.notifications
  );
  const { headerConfig } = useAppSelector((state) => state.app);
  const { dicts } = useAppSelector((state) => state.dict);
  const pageTitle = useAppSelector((state) => state.newOrder.pageTitle);
  const dealer = useAppSelector((state) => state.userInfo.dealer);
  const [user, setUser] = useState<{
    lastName: string;
    firstName: string;
    actions: number[];
  }>({
    firstName: '',
    lastName: '',
    actions: []
  });
  const [isOpenDropdown, setIsOpenDropdown] = useState(false);

  const [goBackLink, setGoBackLink] = useState('');

  const bank =
    dicts?.[DictTypes.Banks]?.getBy.name[safeKey(headerConfig.chosenBankName)];

  const isHasBackArrow =
    headerConfig.hasBackArrow || shouldShowBackArrow(location.pathname);
  const isBankSummaryPage: boolean = !!match(ROUTES.bankSummary(), {
    decode: decodeURIComponent
  })(location.pathname);
  const isMainPage = location.pathname === '/';

  const items: { key: string; label: React.ReactNode }[] = [
    {
      key: 'personal',
      label: (
        <Link
          to={ROUTES.personal}
          className="header__dropdown-item"
          onClick={handleCloseDropdown}
        >
          Личный кабинет
        </Link>
      )
    },
    ...(user.actions.includes(0)
      ? [
          {
            key: 'adminPage',
            label: (
              <Link
                to={getFullAdminRoute(ADMIN_ROUTES.dealers)}
                className="header__dropdown-item"
                onClick={handleCloseDropdown}
              >
                Панель администратора
              </Link>
            )
          }
        ]
      : []),
    {
      key: 'cache-clear',
      label: (
        <button
          onClick={handleClickCacheClear}
          className="header__dropdown-item header__dropdown-item--mobile"
        >
          Очистить кэш
        </button>
      )
    },
    {
      key: 'notification',
      label: (
        <button
          onClick={handleClickNotification}
          className="header__dropdown-item header__dropdown-item--mobile"
        >
          Уведомления
        </button>
      )
    },
    {
      key: 'logout',
      label: (
        <button onClick={handleLogout} className="header__dropdown-item">
          Выйти
        </button>
      )
    }
  ];

  function handleClickNotification() {
    dispatch(onToggleShowNotifications());
    handleCloseDropdown();
  }

  async function handleClickCacheClear() {
    removeCachedData();
    handleCloseDropdown();
    await dispatch(
      getOptimizedDictionaries({
        updatingDicts: Object.values(DictTypes),
        cacheConfig: { ignoreCache: true }
      })
    );
    notification.success({ title: 'Кэш успешно очищен' });
  }

  function handleLogout() {
    removeCachedData();
    clearLocalStorageHelper();
    dispatch(setIsAuth(false));
    handleCloseDropdown();
    navigate(ROUTES.login);
  }

  const handleOpenDropdownChange: DropdownProps['onOpenChange'] = (
    nextOpen,
    info
  ) => {
    if (info.source === 'trigger' || nextOpen) {
      setIsOpenDropdown(nextOpen);
    }
  };

  function handleCloseDropdown() {
    setIsOpenDropdown(false);
  }

  useEffect(() => {
    const accessToken = getUserToken().accessToken;
    if (accessToken) {
      const data = jwtDecode<DecodedJWTToken>(accessToken || '');
      setUser((prevState) => ({
        ...prevState,
        firstName: data?.firstName || '',
        lastName: data?.lastName || '',
        actions: data?.actions || []
      }));
    }
  }, []);

  useEffect(() => {
    if (!goBackLink) return;

    let timeout: NodeJS.Timeout | undefined = undefined;
    if (goBackLink === location.pathname) {
      // Если не редиректит сразу при клике, то редиректим до первого уровня
      timeout = setTimeout(() => {
        const href = goBackLink.replace(/^(\/adminPage)?(.+?)\/.*/, '$1$2');
        navigate(href);
      }, 300);
    }

    return () => {
      clearTimeout(timeout);
      setGoBackLink('');
    };
  }, [goBackLink, location.pathname]);

  return (
    <header className="header">
      <div className="header__container">
        <button
          onClick={() => dispatch(setSidebarExpanded(true))}
          className="header__burger"
        >
          <BurgerMenuIcon />
        </button>

        <div
          className={`header__title-wrapper ${isHasBackArrow ? 'header__title-wrapper--clickable' : ''}`}
          onClick={() => {
            if (!isHasBackArrow) return;
            if (headerConfig.backArrowTo && headerConfig.backArrowTo !== -1) {
              navigate(headerConfig.backArrowTo);
            } else {
              navigate(-1);
              setGoBackLink(location.pathname);
            }
          }}
        >
          {isHasBackArrow && <ChevronDownIcon />}

          {isBankSummaryPage && !!bank && (
            <CircledImage
              src={
                bank.icon ??
                BANKS_ICONS_NAMES[bank.name as keyof typeof BANKS_ICONS_NAMES]
                  ?.icon ??
                ''
              }
              alt={bank.fullname}
              size={40}
            />
          )}

          <div>
            <h2 className="header__title">
              {headerConfig.title ||
                getPageInfo(location.pathname) ||
                pageTitle}
            </h2>

            {(isHasBackArrow || isMainPage) && (
              <p className="header__secondary-text">
                {isMainPage ? 'г. Алматы' : headerConfig.subtitle || 'Назад'}
              </p>
            )}
          </div>
        </div>

        <div className="header__pagination">
          <p className="header__dealer">{dealer?.label}</p>
          <div className="header__language">RU</div>

          <Tooltip
            placement="bottom"
            title="Очистить кэш"
            className="header__cache"
          >
            <button onClick={handleClickCacheClear}>
              <BroomIcon />
            </button>
          </Tooltip>
          <Badge
            className="header__bell"
            dot={!lastNotificationsChecked}
            offset={[-10, 4]}
          >
            <button onClick={handleClickNotification}>
              <BellIcon />
            </button>
          </Badge>

          <Dropdown
            placement="bottomRight"
            arrow
            open={isOpenDropdown}
            onOpenChange={handleOpenDropdownChange}
            popupRender={() => (
              <div className="header__dropdown">
                {items.map((item) => (
                  <React.Fragment key={item.key}>{item.label}</React.Fragment>
                ))}
              </div>
            )}
          >
            <Space className="header__user">
              <Avatar className="header__user-avatar">
                {user.firstName[0]?.toUpperCase()}
                {user.lastName[0]?.toUpperCase()}
              </Avatar>{' '}
              <p className="header__user-name">
                {getUserFullName(user, { pattern: 'first_last' })}
              </p>
              <DownOutlined />
            </Space>
          </Dropdown>
        </div>
      </div>
    </header>
  );
};

export default Header;
